import React, { useState, useRef, useEffect } from 'react';
import { db, latestMessageId } from '../services/db';
import type { Chat, ChatMessage } from '../types';

export default function MessagesView(): JSX.Element {
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const [inputVal, setInputVal] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Which conversation is open, readable from callbacks that were created
  // before it changed (the poll below outlives every render).
  const activeIdRef = useRef<number | null>(null);

  /**
   * Mark a conversation read for this viewer, and drop its dot immediately
   * rather than waiting for the next refetch (PIT-86).
   *
   * Read state is per participant: the marker is keyed by user and the policy
   * admits nobody else's, so this cannot affect what the other side sees.
   *
   * Takes the list to read from because the callers have fresher data than
   * state does — the load and the poll both hold the rows they just fetched.
   * Deliberately not an effect: marking is a write to an external system in
   * response to an event, and doing it in an effect body means calling setState
   * synchronously during render.
   */
  const markRead = (chatId: number, list: Chat[]): void => {
    const chat = list.find((c) => c.id === chatId);
    const newest = chat ? latestMessageId(chat) : 0;
    // Nothing said yet, so nothing to have read.
    if (newest === 0) return;
    setChats((prev) => prev.map((c) => (c.id === chatId ? { ...c, unread: false } : c)));
    db.markChatRead(chatId, newest);
  };

  /** Open a conversation, which is also what marks it read. */
  const openChat = (chatId: number, list: Chat[]): void => {
    setActiveChatId(chatId);
    activeIdRef.current = chatId;
    markRead(chatId, list);
  };

  useEffect(() => {
    db.getChats().then((data: Chat[]) => {
      setChats(data);
      if (data.length > 0) openChat(data[0].id, data);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep conversations fresh without realtime: refetch on window focus and
  // on a slow poll. Skipped in demo mode, where chats live only in memory.
  useEffect(() => {
    if (db.demoMode) return undefined;
    const refetch = () =>
      db.getChats().then((data: Chat[]) => {
        setChats(data);
        // A reply that lands while its thread is open has been seen. Without
        // this the marker would sit behind the newest message and the dot
        // would reappear on a conversation the viewer is looking at.
        const open = activeIdRef.current;
        if (open !== null) markRead(open, data);
      });
    const interval = setInterval(refetch, 15000);
    window.addEventListener('focus', refetch);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refetch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeChat = chats.find((c) => c.id === activeChatId);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeChat?.messages]);

  const sendMessage = (): void => {
    const text = inputVal.trim();
    if (!text) return;
    const msg: ChatMessage = { from: 'brand', text, time: 'Now' };
    setChats((prev) =>
      prev.map((c) =>
        c.id === activeChatId ? { ...c, messages: [...c.messages, msg], preview: text } : c
      )
    );
    setInputVal('');
    db.addMessage(activeChatId, msg);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') sendMessage();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toUpperCase() ?? '';
    const newRevisions = (activeChat?.revisions ?? 0) + 1;
    const msg: ChatMessage = {
      from: 'brand',
      file: file.name,
      fileType: ext,
      delivery: false,
      time: 'Now',
    };
    setChats((prev) =>
      prev.map((c) =>
        c.id === activeChatId
          ? {
              ...c,
              revisions: newRevisions,
              messages: [...c.messages, msg],
              preview: `Uploaded ${file.name}`,
            }
          : c
      )
    );
    e.target.value = '';
    db.addMessage(activeChatId, msg, newRevisions);
  };

  // Demo-mode messages have no db id, so target by index and only persist
  // when a real id exists.
  const markFinalDelivery = (index: number, messageId?: number): void => {
    setChats((prev) =>
      prev.map((c) => {
        if (c.id !== activeChatId) return c;
        const messages = c.messages.map((m, i) => (i === index ? { ...m, delivery: true } : m));
        return { ...c, messages, status: 'Delivered', statusClass: 'status-delivered' };
      })
    );
    if (messageId !== undefined) db.markFinalDelivery(messageId, activeChatId);
  };

  if (loading)
    return (
      <div
        className="view"
        style={{
          textAlign: 'center',
          padding: '60px 0',
          fontFamily: 'var(--cinzel)',
          color: 'var(--text-muted)',
          letterSpacing: 3,
        }}
      >
        LOADING...
      </div>
    );

  if (!activeChat)
    return (
      <div className="view" style={{ textAlign: 'center', padding: '60px 24px' }}>
        <div className="section-header">Correspondence</div>
        <div className="gold-divider">
          <span className="divider-gem">◆</span>
        </div>
        <p
          style={{
            color: 'var(--text-muted)',
            fontFamily: 'var(--cormorant)',
            fontSize: 18,
          }}
        >
          No conversations yet. Invite a creator to a campaign to start one.
        </p>
      </div>
    );

  const revisionsLeft = activeChat.maxRevisions - activeChat.revisions;

  return (
    <div className="view">
      <div className="section-header">Correspondence</div>
      <div className="section-sub">All communication is secured within the platform.</div>

      <div className="chat-layout">
        <div className="chat-list">
          <div className="chat-list-header">Active Deals</div>
          {chats.map((c) => (
            <div
              key={c.id}
              className={`chat-item${c.id === activeChatId ? ' active' : ''}`}
              onClick={() => openChat(c.id, chats)}
            >
              <div className="chat-item-name">
                {c.name}
                {c.unread && <span className="unread-dot" />}
              </div>
              <div className="chat-item-preview">{c.preview}</div>
            </div>
          ))}
        </div>

        <div className="chat-main">
          <div className="chat-header">
            <div className="avatar">{activeChat.init}</div>
            <div style={{ flex: 1 }}>
              <div className="chat-name">{activeChat.name}</div>
              <div className="chat-sub">{activeChat.campaign}</div>
            </div>
            <div className="revision-bar">
              <span className="revision-label">Revisions</span>
              <div className="revision-pips">
                {Array.from({ length: activeChat.maxRevisions }).map((_, i) => (
                  <div
                    key={i}
                    className={`revision-pip${i < activeChat.revisions ? ' used' : ''}`}
                  />
                ))}
              </div>
              <span className="revision-count">{revisionsLeft} left</span>
            </div>
            <div className={`status-badge ${activeChat.statusClass}`}>{activeChat.status}</div>
          </div>

          <div className="chat-messages">
            {activeChat.messages.map((m, i) => {
              if (m.file)
                return (
                  <div className={`msg from-${m.from}`} key={m.id ?? i}>
                    <div className="file-bubble">
                      <div className="file-icon">{m.fileType}</div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{ fontSize: 18, fontWeight: 500, color: 'var(--text-primary)' }}
                        >
                          {m.file}
                        </div>
                        <div style={{ fontSize: 15, color: 'var(--text-muted)' }}>
                          {m.delivery ? '◆ Final Delivery' : 'Tap to review'}
                        </div>
                      </div>
                      {!m.delivery && m.from === 'creator' && (
                        <button className="delivery-btn" onClick={() => markFinalDelivery(i, m.id)}>
                          Mark Final
                        </button>
                      )}
                      {m.delivery && <span className="delivery-badge">◆ Delivered</span>}
                    </div>
                    <div className="msg-meta">{m.time}</div>
                  </div>
                );
              return (
                <div className={`msg from-${m.from}`} key={m.id ?? i}>
                  <div className="msg-bubble">{m.text}</div>
                  <div className="msg-meta">{m.time}</div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-row">
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <button
              className="attach-btn"
              onClick={() => fileInputRef.current?.click()}
              title="Attach file"
            >
              ⊕
            </button>
            <input
              className="chat-input"
              placeholder={`Message ${activeChat.name.split(' ')[0]}...`}
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={handleKey}
            />
            <button className="send-btn" onClick={sendMessage}>
              &#10148;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
